import { Link } from 'react-router';
import { BRAND, brandName } from '@/brand/brand';
import { Logo } from '@/brand/Logo';
import { GearIcon } from '@/components/Icons';
import { Lantern, StarBadge } from '@/components/Lantern';
import { Mark } from '@/components/Mark';
import { Screen } from '@/components/Screen';
import { PRAYERS, markNoteKey } from '@/domain/prayer';
import { countRecorded, markFor, streakDays } from '@/domain/streaks';
import { currentPrayer, isDue } from '@bonyaan/prayer-times';
import { useAppState } from '@/state/useAppState';
import { useDayRecord } from '@/state/useDayRecord';
import { useStreaks } from '@/state/useStreaks';
import { useT } from '@/state/useT';

const CHIPS = [
  { key: 'onTime', tint: 'var(--t2)', dot: 'var(--q2)' },
  { key: 'jamaah', tint: 'var(--t3)', dot: 'var(--q3)' },
  { key: 'takbirah', tint: 'var(--t4)', dot: 'var(--q4)' },
] as const;

export function Home() {
  const { profile, times, today, now } = useAppState();
  const { t, number, time, hijri, locale } = useT();
  const { day } = useDayRecord(today);
  const streaks = useStreaks(today, day);

  // Which prayer's time it is right now. `now` ticks in the provider, so the
  // highlight moves at each adhān on a screen left open, not only on a reload.
  const current = currentPrayer(times, now);
  const recorded = countRecorded(day);
  // The run counts prayers, so it moves five times a day rather than sitting
  // still until midnight. The days beside it are that figure over five: the
  // headline a person recognises, without the number they watch going stale.
  const days = streakDays(streaks.prayed);
  const best = CHIPS.reduce<{ key: (typeof CHIPS)[number]['key']; count: number } | null>(
    (acc, c) => (streaks[c.key] > (acc?.count ?? 0) ? { key: c.key, count: streaks[c.key] } : acc),
    null,
  );

  return (
    <Screen
      nav
      header={
        <header className="app-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* The mark is a clock of today's real prayer-day — the calculated
                times, and the app's own ticking `now`, so the alidade moves
                and a prayer's mark comes up at its adhān on a screen left
                open. Tapping it goes to the thing a clock is for: recording
                the next one. */}
            <Link
              to="/log"
              data-testid="mark-link"
              aria-label={t('home.markLink')}
              style={{ display: 'grid', placeItems: 'center' }}
            >
              <Logo times={times} now={now} title={t('home.markLink')} />
            </Link>
            <div>
              <div
                style={{ fontFamily: 'var(--fd)', fontSize: 21, fontWeight: 800, lineHeight: 1 }}
              >
                {brandName(locale)}
              </div>
              <div className="sky-label" style={{ marginTop: 4 }}>
                {profile?.name
                  ? t('app.streakOwner', { name: profile.name })
                  : t('app.streakLabel')}
              </div>
            </div>
          </div>
          <Link to="/settings" className="modebtn" aria-label={t('settings.title')}>
            <GearIcon />
          </Link>
        </header>
      }
    >
      <section
        className="hero"
        style={{ marginTop: 14, padding: 'var(--heropad-t) 16px var(--heropad-b) 16px' }}
      >
        <div className="eyebrow">{t('home.heroEyebrow')}</div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
            <span className="heronum" data-testid="streak-prayers">
              {number(streaks.prayed)}
            </span>
            <span style={{ fontFamily: 'var(--fd)', fontSize: 18, fontWeight: 700, lineHeight: 1 }}>
              {t('home.prayersUnit', { count: streaks.prayed })}
            </span>
            <span className="heroequiv">
              <span data-testid="streak-days">{number(days)}</span>{' '}
              {t('home.days', { count: days })}
            </span>
          </div>
          <Lantern />
        </div>
        <div style={{ display: 'flex', gap: 5, marginTop: 10 }} aria-hidden="true">
          {PRAYERS.map((p, i) => (
            <div
              key={p}
              style={{
                flex: 1,
                height: 7,
                borderRadius: 'var(--bar)',
                background: i < recorded ? 'var(--q1)' : 'var(--q-off)',
              }}
            />
          ))}
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: 11.5,
            lineHeight: 1.2,
            color: 'var(--panel-dim)',
            fontWeight: 600,
          }}
        >
          {t('home.recorded', { count: number(recorded), total: number(PRAYERS.length) })}
        </div>
      </section>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 8,
          marginTop: 10,
        }}
      >
        {CHIPS.map((chip) => (
          <div
            key={chip.key}
            className="chip"
            style={{ background: chip.tint }}
            // The numeral is a count of prayers, which the chip has no room to
            // say — only the hero above spells the unit out, and a reader that
            // never reaches it would otherwise hear a bare number. `img` is
            // what makes the label stand in for the whole chip rather than be
            // ignored on a plain div.
            role="img"
            aria-label={t('home.reward.title', {
              count: streaks[chip.key],
              quality: t(`quality.${chip.key}.short`),
            })}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="dot" style={{ background: chip.dot }} />
              <span className="lbl">{t(`quality.${chip.key}.short`)}</span>
            </div>
            <div className="num" data-testid={`streak-${chip.key}`}>
              {number(streaks[chip.key])}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          height: 24,
          marginTop: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--fd)',
            fontSize: 19,
            fontWeight: 800,
            lineHeight: 1.2,
            margin: 0,
          }}
        >
          {t('home.today')}
        </h1>
        <div className="sky-label" style={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.2 }}>
          {hijri()}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
        {PRAYERS.map((id) => {
          const mark = markFor(day, id);
          const at = times[id];
          const due = isDue(at, times.fajr, now);
          const note = t(markNoteKey(mark, due));
          const name = t(`prayer.${id}`);
          const script = t(`prayer.ar.${id}`);
          const full = mark.prayed && mark.jamaah && mark.takbirah;
          const later = !mark.prayed && !due;
          const isNow = id === current;
          return (
            <Link
              key={id}
              to={`/log/${id}`}
              data-testid={`prayer-${id}`}
              className={`prow${full ? ' pfull' : ''}${later ? ' plater' : ''}${isNow ? ' pnow' : ''}`}
              // The label spells the tag out: aria-label replaces the row's
              // text wholesale, so a "Now" left only in the markup is a "Now"
              // a screen reader never hears.
              aria-current={isNow ? 'time' : undefined}
              aria-label={
                isNow
                  ? `${name} ${time(at)}, ${t('home.now')}, ${note}`
                  : `${name} ${time(at)}, ${note}`
              }
            >
              <Mark mark={mark} />
              <span style={{ flexGrow: 1 }}>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                  <span className="pname">{name}</span>
                  {script ? (
                    <span className="par" lang="ar" dir="rtl">
                      {script}
                    </span>
                  ) : null}
                  {isNow ? <span className="pnowtag">{t('home.now')}</span> : null}
                </span>
                <span className="pnote">{note}</span>
              </span>
              <span className="ptime">{time(at)}</span>
            </Link>
          );
        })}
      </div>

      <div
        className="reward kids-only"
        style={{
          marginTop: 10,
          padding: 'var(--rewpad) 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <StarBadge />
        <span style={{ flexGrow: 1 }}>
          <span
            style={{
              display: 'block',
              fontFamily: 'var(--fd)',
              fontSize: 14.5,
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {best
              ? t('home.reward.title', {
                  count: best.count,
                  quality: t(`quality.${best.key}.short`),
                })
              : t(recorded > 0 ? 'home.reward.litTitle' : 'home.reward.emptyTitle')}
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 11,
              lineHeight: 1.2,
              color: 'var(--panel-dim)',
              fontWeight: 600,
              marginTop: 1,
            }}
          >
            {best
              ? t('home.reward.sub')
              : t(recorded > 0 ? 'home.reward.litSub' : 'home.reward.emptySub')}
          </span>
        </span>
      </div>

      <div className="hadith adult-only" style={{ marginTop: 10, padding: 'var(--rewpad) 13px' }}>
        <div style={{ fontFamily: 'var(--fa)', fontSize: 14, lineHeight: 1.4 }}>
          {t('home.hadith')}
        </div>
        <div
          style={{
            fontSize: 11,
            lineHeight: 1.2,
            color: 'var(--panel-dim)',
            fontWeight: 700,
            marginTop: 5,
          }}
        >
          {t('home.hadithSource')}
        </div>
      </div>

      {BRAND.provisional ? (
        <div className="muted-note" style={{ marginTop: 12, textAlign: 'center', fontSize: 10.5 }}>
          {t('app.provisionalName')}
        </div>
      ) : null}
    </Screen>
  );
}
